import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Trans, useLingui } from '@lingui/react';
import { useCreateArticlesOfAssocMutation, useDeleteArticlesOfAssocMutation } from '../../graphql/react-operations';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { ArticlesOfAssocsDocument, useArticlesOfAssocsSuspenseQuery } from './ArticlesOfAssocs.graphql';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { useState } from 'react';
import { AlertConfirmDialog } from '../../components/simple-dialogs/AlertConfirmDialog';
import { ItemList } from '@startuptools/ui/ItemList';
import { useNavigate } from '../../components/Navigate';
import { articlesOfAssocLinkEdit } from '../../components/Links';
import { Route, Routes } from 'react-router-dom';
import { ArticlesOfAssocWithRoutes } from './ArticlesOfAssoc';

export const ArticlesOfAssocsRoutes = () => {
  return (
    <Routes>
      <Route path="" element={<ArticlesOfAssocs />} />
      <Route path=":articlesOfAssocId" element={<ArticlesOfAssocWithRoutes />}>
        <Route path="*" element={<ArticlesOfAssocWithRoutes />} />
      </Route>
    </Routes>
  );
};

export const ArticlesOfAssocs = () => {
  const companyId = useCompanyId();
  const { link, navigate } = useNavigate();
  const { i18n } = useLingui();
  const [showDelete, setShowDelete] = useState<{ id: string } | null>(null);
  const {
    data: { articlesOfAssocs },
  } = useArticlesOfAssocsSuspenseQuery({ variables: { companyId }, fetchPolicy: 'cache-and-network' });
  const [doCreate, { loading: createLoading }] = useCreateArticlesOfAssocMutation({
    refetchQueries: [ArticlesOfAssocsDocument],
  });

  const [doDelete, { loading: deleteLoading }] = useDeleteArticlesOfAssocMutation({
    refetchQueries: [ArticlesOfAssocsDocument],
  });

  const navigateToAoa = (id: string) => {
    return navigate(articlesOfAssocLinkEdit({ id }));
  };

  const handleCreate = async () => {
    const newAg = await doCreate({ variables: { companyId, input: {} } });
    if (newAg.data) {
      return navigateToAoa(newAg.data.createArticlesOfAssoc.id);
    }
  };

  return (
    <>
      <Stack gap={3}>
        <Stack direction="row" justifyContent="space-between">
          <Typography level="h2">
            <Trans id="Articles of Associations" />
          </Typography>
        </Stack>

        <ItemList
          globalActions={[
            {
              label: <Trans id="New Articles of Associations" />,
              onClick: handleCreate,
              buttonProps: {
                loading: createLoading,
              },
            },
          ]}
          onDelete={aoa => setShowDelete(aoa)}
          linkTo={aoa => link(articlesOfAssocLinkEdit(aoa))}
          items={articlesOfAssocs.map(aoa => ({
            ...aoa,
            deletable: true,
          }))}
          columns={[
            {
              header: <Trans id="Decision Date" />,
              render: aoa => <DateFormat locale={i18n.locale}>{aoa.meetingConcern?.meeting.startTime}</DateFormat>,
            },
            {
              header: <Trans id="Created Date" />,
              render: aoa => <DateFormat locale={i18n.locale}>{aoa.createdAt}</DateFormat>,
            },
            {
              header: <Trans id="Status" />,
              render: aoa => (!aoa.meetingConcern ? <Trans id="Draft" /> : '--'),
            },
          ]}
        />
      </Stack>
      {showDelete && (
        <AlertConfirmDialog
          loading={deleteLoading}
          onClose={() => setShowDelete(null)}
          content={<Trans id="Are you sure that you want to delete the articles of associations?" />}
          onConfirm={async () => {
            await doDelete({ variables: { companyId, id: showDelete.id } });
            setShowDelete(null);
          }}
        />
      )}
    </>
  );
};
